// Moving players in a real league, from the screens. PLAN-2026-09-24 W8.
//
// Six writes, each a thin call over `send`: an add or a drop and its undo
// (`POST …/transactions`, `…/transactions/:txId/undo`), and a claim placed,
// edited, withdrawn or reordered (`…/waivers/claims…`). The rules are the
// server's; what comes back is shown as it came, refusal sentence and all.
//
// ONE KEY PER ACTION, THE SAME KEY WHEN THE SAME ACTION IS TRIED AGAIN (B-150).
// The two POSTs take an `Idempotency-Key`, and the server answers a repeat of
// the same key and body with the first answer. That only protects anybody if
// the app sends the SAME key when it sends the same move again — after a
// timeout, or "Could not reach the site" — and a NEW one for a new move. So
// the key is held by `AttemptKeys`, bound to the body it was minted for: the
// same body gets the same key until an answer settles it; a different body,
// or the same one after a definite answer, gets a fresh one. This is the
// league wizard's rule (`leagues.ts`), with the body added — a person who
// changes the drop between two presses has made a different move, and the old
// key on a new body is refused as reused.
//
// AFTER A WRITE, WHAT SHOWS IT IS READ AGAIN. `movesChanged` bumps a counter
// per league; every league read through `useLeagueView` asks again when it
// moves, keeping what it holds on screen while it does (a stale read, never a
// blank one). Bumped after every write that got an answer or may have landed —
// a refusal often means the league moved under the screen, and a timeout may
// have made the move — because a read that is not needed costs one request
// and a stale roster costs a wrong decision. THE SIGNAL IS THE LEAGUE'S, not
// the move's: a team renamed or recolored (`team.ts`, SCENARIOS L2) bumps it too, and
// the pre-draft seats (`useLeagueSetup`) listen as well as the league views.

import { useSyncExternalStore } from 'react';
import { z } from 'zod';
import {
  ClaimView,
  ScheduleView,
  TransactionView,
  WaiversView,
  type ErrorCode,
  type AddDropRequest,
  type ClaimOrderRequest,
  type ClaimRequest,
} from '@fsp/domain';
import { send, type Reply } from './api';
import { newAttemptKey } from './leagues';

// ── The refresh signal ─────────────────────────────────────────────────────

const versions = new Map<string, number>();
const listeners = new Set<() => void>();

/** A move in this league landed, or may have: every read of it asks again. */
export function movesChanged(leagueId: string): void {
  versions.set(leagueId, (versions.get(leagueId) ?? 0) + 1);
  for (const listen of listeners) listen();
}

function subscribe(listen: () => void): () => void {
  listeners.add(listen);
  return () => listeners.delete(listen);
}

/** How many moves this tab has made in `leagueId`: a read's cue to ask again. */
export function useMovesVersion(leagueId: string | null): number {
  return useSyncExternalStore(subscribe, () =>
    leagueId === null ? 0 : (versions.get(leagueId) ?? 0),
  );
}

/** The league a `/leagues/<id>/…` path is about, or null for any other path. */
export function leagueOfPath(path: string | null): string | null {
  if (path === null) return null;
  const match = /^\/leagues\/([^/?]+)/.exec(path);
  return match ? decodeURIComponent(match[1]!) : null;
}

// ── The key for one action ─────────────────────────────────────────────────

/**
 * Refusals that judged the MOVE: the server looked at it and said no, so the
 * next press is a new attempt and gets a new key. Everything else leaves the
 * move's fate unknown or untried — a 503 (`unavailable`, which is also what a
 * move that committed and then failed to answer looks like), a throttle, a
 * signed-out session — and keeps the key.
 */
const JUDGED: ReadonlySet<ErrorCode> = new Set([
  'invalid',
  'conflict',
  'forbidden',
  'not_found',
  'locked',
]);

/**
 * The `Idempotency-Key` for one action, reused only for that same action.
 *
 * `keyFor(body)` answers the held key while the body is the one it was minted
 * for and nothing has settled it; anything else mints a new one. `settled`
 * takes the reply, and only two things spend the key: an `ok`, and a refusal
 * of the move itself (`JUDGED`). A key is KEPT for the same body after no
 * answer (`unreachable`, a timeout), after a 503, and after the server's
 * "That request is already being handled." (`idempotency-key-in-flight`),
 * because in each the move may have been made — and the same key sent again
 * is answered with it, where a new key is told the player is already yours
 * (B-150; the review of W8 found both of the last two minting a new key).
 */
export class AttemptKeys {
  private held: { key: string; body: string } | null = null;

  constructor(private readonly mint: () => string = newAttemptKey) {}

  keyFor(body: unknown): string {
    const text = JSON.stringify(body);
    if (this.held === null || this.held.body !== text) {
      this.held = { key: this.mint(), body: text };
    }
    return this.held.key;
  }

  settled(reply: Reply<unknown>): void {
    if (spendsKey(reply)) this.held = null;
  }
}

/** Whether this reply settles the action for good: done, or refused on its merits. */
export function spendsKey(reply: Reply<unknown>): boolean {
  if (reply.status === 'ok') return true;
  if (reply.status === 'unreachable') return false;
  if (reply.details?.reason === 'idempotency-key-in-flight') return false;
  return JUDGED.has(reply.code);
}

// ── The writes ─────────────────────────────────────────────────────────────

const ClaimViews = z.array(ClaimView);

/**
 * The reply, and then the league's reads asked again — AFTER the caller has
 * had the answer. A sheet that closes on success would otherwise be asked to
 * read itself again in the same moment and then abort that read as it closed.
 */
export async function thenRefresh<T>(
  leagueId: string,
  reply: Promise<Reply<T>>,
): Promise<Reply<T>> {
  const settled = await reply;
  setTimeout(() => movesChanged(leagueId), 0);
  return settled;
}

/**
 * Draw the schedule again, a commissioner's, before the season opens
 * (PLAN-2026-10-29-the-schedule-before-the-season §2).
 */
export function drawSchedule(leagueId: string): Promise<Reply<ScheduleView>> {
  return thenRefresh(
    leagueId,
    send('POST', `/leagues/${leagueId}/schedule/draw`, undefined, ScheduleView),
  );
}

/** Add a free agent, drop players, or both: one move. */
export function addDrop(
  leagueId: string,
  body: AddDropRequest,
  key: string,
): Promise<Reply<TransactionView>> {
  return thenRefresh(
    leagueId,
    send('POST', `/leagues/${leagueId}/transactions`, body, TransactionView, {
      headers: { 'idempotency-key': key },
    }),
  );
}

/** Take back an add or a drop of mine inside its five minutes. Nothing to key: a second undo is refused. */
export function undoMove(leagueId: string, txId: string): Promise<Reply<TransactionView>> {
  return thenRefresh(
    leagueId,
    send(
      'POST',
      `/leagues/${leagueId}/transactions/${encodeURIComponent(txId)}/undo`,
      undefined,
      TransactionView,
    ),
  );
}

/**
 * A commissioner taking back a processed add, drop, claim or trade, with a
 * reason (J2). Nothing to key: a second reversal of one move is refused.
 */
export function reverseMove(
  leagueId: string,
  txId: string,
  reason: string,
): Promise<Reply<TransactionView>> {
  return thenRefresh(
    leagueId,
    send(
      'POST',
      `/leagues/${leagueId}/transactions/${encodeURIComponent(txId)}/reverse`,
      { reason },
      TransactionView,
    ),
  );
}

/**
 * A commissioner moving a team to a place in a stored waiver order, with a
 * reason (J7). Nothing to key: the same place sent twice is refused as the
 * place it already has.
 */
export function setWaiverPriority(
  leagueId: string,
  teamId: string,
  rank: number,
  reason: string,
): Promise<Reply<WaiversView>> {
  return thenRefresh(
    leagueId,
    send(
      'PUT',
      `/leagues/${leagueId}/teams/${encodeURIComponent(teamId)}/waiver-priority`,
      { rank, reason },
      WaiversView,
    ),
  );
}

/** A commissioner setting a team's budget left, with a reason (J7). Refused as a repeat when it already is. */
export function setBudget(
  leagueId: string,
  teamId: string,
  left: number,
  reason: string,
): Promise<Reply<WaiversView>> {
  return thenRefresh(
    leagueId,
    send(
      'PUT',
      `/leagues/${leagueId}/teams/${encodeURIComponent(teamId)}/budget`,
      { left, reason },
      WaiversView,
    ),
  );
}

/** Put in a claim. */
export function placeClaim(
  leagueId: string,
  body: ClaimRequest,
  key: string,
): Promise<Reply<ClaimView>> {
  return thenRefresh(
    leagueId,
    send('POST', `/leagues/${leagueId}/waivers/claims`, body, ClaimView, {
      headers: { 'idempotency-key': key },
    }),
  );
}

/** One of my pending claims, as it should now stand. An edit sent twice leaves it as the body says. */
export function editClaim(
  leagueId: string,
  claimId: string,
  body: ClaimRequest,
): Promise<Reply<ClaimView>> {
  return thenRefresh(
    leagueId,
    send(
      'PUT',
      `/leagues/${leagueId}/waivers/claims/${encodeURIComponent(claimId)}`,
      body,
      ClaimView,
    ),
  );
}

/** Withdraw a pending claim: my claims as they stand after. */
export function withdrawClaim(leagueId: string, claimId: string): Promise<Reply<ClaimView[]>> {
  return thenRefresh(
    leagueId,
    send(
      'DELETE',
      `/leagues/${leagueId}/waivers/claims/${encodeURIComponent(claimId)}`,
      undefined,
      ClaimViews,
    ),
  );
}

/** My pending claims, in the order to try them. */
export function reorderClaims(
  leagueId: string,
  body: ClaimOrderRequest,
): Promise<Reply<ClaimView[]>> {
  return thenRefresh(
    leagueId,
    send('PUT', `/leagues/${leagueId}/waivers/claims/order`, body, ClaimViews),
  );
}
